import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  GetAllReportsRequest,
  GetAllReportsResponse,
  LatestReportRequest,
  LatestReportResponse,
  ReportHistoryRequest,
  ReportHistoryResponse,
} from '../interfaces/report-query.model';
import { ApiResponse } from '../interfaces/api-response.model';
import { API_ENDPOINTS } from '../constants/api.constants';
import { Observable } from 'rxjs';

/**
 * 會員中心檢查報告service
 */
@Injectable({
  providedIn: 'root',
})
export class ReportQueryService {
  private http: HttpClient = inject(HttpClient);

  /**
   * 查詢客戶最新報告資料 (CB-054)
   * @param request 查詢最新報告請求參數
   */
  getLatestReport(request: LatestReportRequest): Observable<ApiResponse<LatestReportResponse>> {
    return this.http.post<ApiResponse<LatestReportResponse>>(
      API_ENDPOINTS.LATEST_REPORT,
      request
    );
  }

  /**
   * 查詢客戶檢項歷年紀錄 (CB-055)
   * @param request 查詢歷年紀錄請求參數
   */
  getReportHistory(request: ReportHistoryRequest): Observable<ApiResponse<ReportHistoryResponse>> {
    return this.http.post<ApiResponse<ReportHistoryResponse>>(
      API_ENDPOINTS.REPORT_HISTORY,
      request
    );
  }

  /**
   * 查詢所有報告
   * @param request 查詢所有報告請求參數
   */
  getAllReports(
    request: GetAllReportsRequest
  ): Promise<ApiResponse<GetAllReportsResponse>> {
    // TODO 改使用post請求
    console.log('API endpoint: ' , API_ENDPOINTS.GET_ALL_REPORTS);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.post<ApiResponse<GetAllReportsResponse>>(
      API_ENDPOINTS.GET_ALL_REPORTS,
      request,
      { headers }
    ).toPromise() as Promise<ApiResponse<GetAllReportsResponse>>;
    // return this.axiosHttpService.post(API_ENDPOINTS.GET_ALL_REPORTS, request);
    // return this.axiosHttpService.get(API_ENDPOINTS.GET_ALL_REPORTS);
  }

  /**
   * 下載報告
   * @param caseNo 健檢編號
   */
  downloadReport(caseNo: string): Promise<void> {
    console.log('API endpoint: ' , API_ENDPOINTS.DOWNLOAD_REPORT);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const requestBody = { caseNo };

    return new Promise((resolve, reject) => {
      this.http.post(
        API_ENDPOINTS.DOWNLOAD_REPORT,
        requestBody,
        {
          headers,
          responseType: 'blob',
          observe: 'response'
        }
      ).subscribe({
        next: (resp) => {
          try {
            const blob = resp.body as Blob;
            const cd = resp.headers.get('content-disposition');
            // 用我們的工具解析
            const filename = this.filenameFromContentDisposition(cd) ?? 'download.pdf';

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;       // ← 自動下載並套你想要的中文檔名
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            resolve(); // 下載成功
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  // 解析 RFC5987: filename*=UTF-8''%E4%BD%A0%E5%A5%BD.pdf
  private decodeRFC5987(value: string): string {
    const cleaned = value.trim().replace(/^"(.*)"$/, '$1');
    // 形式：charset'lang'value
    const match = cleaned.match(/^([^']*)'(?:[^']*)'(.+)$/);
    if (!match) return decodeURIComponent(cleaned);
    // const charset = (match[1] || 'UTF-8').toLowerCase(); // 目前未使用
    const encoded = match[2];

    // 目前後端是 UTF-8，就用 decodeURIComponent 直接解
    // 若未來不是 UTF-8，可用 TextDecoder 配合手動把 %XX 轉成位元組再解碼
    const decoded = decodeURIComponent(encoded);
    return decoded;
  }

  // 解析 RFC2047: =?UTF-8?Q?=E4=BD=A0=E5=A5=BD.pdf?= 或 B-base64
  private decodeRFC2047(value: string): string {
    // 可能被引號包著
    const cleaned = value.trim().replace(/^"(.*)"$/, '$1');

    // 抓 =?charset?encoding?encoded?= 結構
    const r = cleaned.match(/=\?([^?]+)\?([bBqQ])\?([^?]+)\?=/);
    if (!r) {
      // 沒有 encoded-word，就回原字串（可能已是純文字）
      return cleaned;
      }
    const charset = r[1].toLowerCase();
    const encoding = r[2].toUpperCase();
    const encoded = r[3];

    let bytes: Uint8Array;

    if (encoding === 'B') {
      // Base64
      const binStr = atob(encoded);
      bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
    } else {
      // Q-encoding：_ 代表空白，=XX 代表位元組
      const q = encoded.replace(/_/g, ' ');
      // 把 =XX 轉成百分比再 decodeURIComponent
      const percentified = q.replace(/=([0-9A-Fa-f]{2})/g, '%$1');
      const u8 = new TextEncoder().encode(decodeURIComponent(percentified));
      bytes = u8;
    }

    try {
      // 大多數情況是 UTF-8
      const decoder = new TextDecoder(charset as any, { fatal: false });
      return decoder.decode(bytes);
    } catch {
      // 不認得的 charset 就先用 UTF-8 試試
      const decoder = new TextDecoder('utf-8', { fatal: false });
      return decoder.decode(bytes);
    }
  }

  private filenameFromContentDisposition(headerValue?: string | null): string | null {
    if (!headerValue) return null;

    // 1) 先抓 filename*
    const star = headerValue.match(/filename\*\s*=\s*([^;]+)/i);
    if (star) {
      try {
        return this.decodeRFC5987(star[1]);
      } catch {
        // 忽略，試下一個
      }
    }

    // 2) 再抓傳統 filename
    const normal = headerValue.match(/filename\s*=\s*([^;]+)/i);
    if (normal) {
      try {
        return this.decodeRFC2047(normal[1]);
      } catch {
        // 解析失敗就回退去乾淨字串
        return normal[1].trim().replace(/^"(.*)"$/, '$1');
      }
    }

    return null;
  }
}
